import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { BarLoader, Icon, Pagination, usePagination } from '@st-operations/uikit/src/v2'
import toast from '@st-operations/uikit/lib/toast'

import { getFeatures, getOrganizationAuditLog } from '_services/predactiv-admin'

import ValueDiff from './value-diff'

import './styles.scss'

const FIELD_LABELS = {
  name: 'Name',
  active: 'Active',
  features: 'Features',
  audience_features: 'Audience Features',
  audience_insights: 'Audience Insights',
  purchase_based_data: 'Purchase-Based Data',
  disable_audience_builder: 'Disable Audience Builder',
  hide_pending_data_insights: 'Hide Pending-Data Insights',
  online_media_measurement: 'Online Media Measurement',
  omm_destinations: 'OMM Delivery Destinations',
  url_audience: 'URL Audience',
  enable_revenue_report: 'Revenue Report',
  taxonomy_features: 'Taxonomy Features',
  dataset_limit: 'Dataset Limit',
  rate_limit: 'Rate Limit',
  source_partners: 'Source Partners',
  data_sources: 'Data Sources',
  destinations: 'Destinations',
  invited_user: 'Invited User',
  role: 'Role',
  user: 'User',
}

const formatField = (field) =>
  FIELD_LABELS[field] || field?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// A save fans out to one request per field and those rows land in completion order,
// so a card's diffs have to be sorted into a fixed order — otherwise two identical
// saves render differently. FIELD_LABELS is already in a sensible reading order;
// anything unrecognized sorts after it, alphabetically.
const FIELD_ORDER = Object.keys(FIELD_LABELS)

const rankField = (change) => {
  const index = FIELD_ORDER.indexOf(change?.field)
  return index === -1 ? FIELD_ORDER.length : index
}

const compareChanges = (a, b) =>
  rankField(a) - rankField(b) || String(a?.field ?? '').localeCompare(String(b?.field ?? ''))

const formatAction = (action) => String(action || '').replace(/-/g, ' ')

const PAGE_SIZE = 20

// Drop features that no longer exist and relabel the rest with their current name,
// so history doesn't surface stale/deleted feature flags.
const sanitizeFeatureList = (list, featureMap) =>
  (list || [])
    .filter((feature) => feature && featureMap.has(feature._id))
    .map((feature) => ({ ...feature, name: featureMap.get(feature._id) }))

// A save issues one request per section, so it lands as several audit rows sharing a
// change group. Fold those back into a single history entry. Rows with no group —
// older data, or a standalone action like an invite — stand on their own.
export const groupEntries = (list) => {
  const grouped = []
  const byGroup = new Map()

  ;(list || []).forEach((entry, idx) => {
    const key = entry.groupId
    if (!key) {
      grouped.push({
        ...entry,
        key: entry._id || `entry-${idx}`,
        changes: [...(entry.changes || [])],
      })
      return
    }

    const existing = byGroup.get(key)
    if (!existing) {
      // Entries arrive newest-first, so the first row of a group supplies the
      // timestamp and user for the whole card.
      const merged = { ...entry, key: `group-${key}`, changes: [...(entry.changes || [])] }
      byGroup.set(key, merged)
      grouped.push(merged)
      return
    }

    existing.changes.push(...(entry.changes || []))
    // A create plus the config updates that followed it reads as a create.
    if (entry.action === 'create') existing.action = 'create'
  })

  grouped.forEach((entry) => entry.changes.sort(compareChanges))

  return grouped
}

const OrganizationHistory = ({ organizationId, organizationName, env, onCancel }) => {
  const [entries, setEntries] = useState(null)
  const [truncated, setTruncated] = useState(false)
  const [featureMap, setFeatureMap] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getOrganizationAuditLog({ env, id: organizationId }),
      getFeatures(env).catch(() => null),
    ])
      .then(([log, featuresResp]) => {
        if (cancelled) return
        setEntries(log?.items || [])
        setTruncated(Boolean(log?.hasMore))
        setFeatureMap(
          featuresResp?.items
            ? new Map(featuresResp.items.map((f) => [f._id, f.label || f.name]))
            : null
        )
      })
      .catch(() => {
        if (cancelled) return
        setEntries([])
        setTruncated(false)
        toast.error('Error fetching organization history')
      })
    return () => {
      cancelled = true
    }
  }, [env, organizationId])

  // Once the current feature set is known, filter stale feature flags out of the
  // diffs, then fold each save's per-field rows back into one entry.
  const visibleEntries = useMemo(() => {
    if (!entries) return entries
    const sanitized = !featureMap
      ? entries
      : entries.map((entry) => ({
          ...entry,
          changes: (entry.changes || []).map((change) =>
            change.field === 'features'
              ? {
                  ...change,
                  from: sanitizeFeatureList(change.from, featureMap),
                  to: sanitizeFeatureList(change.to, featureMap),
                }
              : change
          ),
        }))
    return groupEntries(sanitized)
  }, [entries, featureMap])

  const { page, toPage } = usePagination(visibleEntries || [], PAGE_SIZE)

  return (
    <div className="apa-org-history">
      <div className="apa-org-history__header">
        <button className="apa-org-history__back-button" onClick={onCancel}>
          <Icon icon="angleleft" type="outline" color="#3d3d3d" height="28px" width="28px" />
        </button>
        <div>
          <h2>Organization History</h2>
          <If condition={organizationName}>
            <span className="apa-org-history__org-name">{organizationName}</span>
          </If>
        </div>
      </div>

      <Choose>
        <When condition={visibleEntries === null}>
          <BarLoader />
        </When>
        <When condition={visibleEntries.length === 0}>
          <p className="apa-org-history__empty">No history recorded for this organization yet.</p>
        </When>
        <Otherwise>
          <ul className="apa-org-history__list">
            <For each="entry" index="idx" of={page.content}>
              <li key={entry.key || entry._id || idx} className="apa-org-history__entry">
                <div className="apa-org-history__entry-meta">
                  <span className="apa-org-history__time">
                    {dayjs(entry.timestamp).format('D MMM YYYY, HH:mm')}
                  </span>
                  <span
                    className={`apa-org-history__action apa-org-history__action--${entry.action}`}
                  >
                    {formatAction(entry.action)}
                  </span>
                  <span className="apa-org-history__user">
                    {entry.user?.email || 'Unknown user'}
                  </span>
                </div>
                <ul className="apa-org-history__changes value-diff__tree">
                  <For each="change" index="cidx" of={entry.changes || []}>
                    <ValueDiff
                      key={change.field || cidx}
                      label={formatField(change.field)}
                      from={change.from}
                      to={change.to}
                    />
                  </For>
                </ul>
              </li>
            </For>
          </ul>
          <If condition={truncated}>
            <p className="apa-org-history__truncated">
              Showing the most recent changes only. Older history is not displayed.
            </p>
          </If>
          <If condition={page.quantity > 1}>
            <div className="apa-org-history__pagination">
              <Pagination
                options={{ length: page.quantity }}
                page={page.number - 1}
                onPageChange={(p) => toPage(p.label)}
              />
            </div>
          </If>
        </Otherwise>
      </Choose>
    </div>
  )
}

OrganizationHistory.propTypes = {
  organizationId: PropTypes.string.isRequired,
  organizationName: PropTypes.string,
  env: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default OrganizationHistory
